<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useFocusTrap } from '@/composables/useFocusTrap'
import UiIcon from '@/components/common/UiIcon.vue'
import { useModalStore, type ModalStateAction } from '@/stores/modal.store'

const modal = useModalStore()
const { isOpen, view, data, actions } = storeToRefs(modal)
const { t } = useI18n()

const dialogRef = ref<HTMLElement | null>(null)
const currentPayload = ref<unknown>(null)
const titleId = 'app-modal-title'
const descriptionId = 'app-modal-description'
const previousBodyOverflow = ref<string | null>(null)
const previousAppAriaHidden = ref<string | null>(null)
const hadAppAriaHidden = ref(false)

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

const resolvedTitle = computed(() => {
  const payload = data.value as Record<string, unknown> | null
  const rawTitle =
    payload && typeof payload.title === 'string'
      ? payload.title
      : payload && typeof payload.label === 'string'
        ? payload.label
        : ''

  const title = stripHtml(rawTitle)
  return title
})

const accessibleTitle = computed(() => resolvedTitle.value || 'Dialog')

const modalActions = computed<ModalStateAction[]>(() => {
  if (actions.value.length > 0) {
    return actions.value
  }

  return [
    {
      label: t('common.close'),
      color: 'btn-ui--secondary',
    },
  ]
})

watch(
  data,
  (value) => {
    currentPayload.value = value
  },
  { immediate: true },
)

function applyModalSideEffects(open: boolean) {
  if (typeof document === 'undefined') return

  const appRoot = document.getElementById('app')

  if (open) {
    if (previousBodyOverflow.value === null) {
      previousBodyOverflow.value = document.body.style.overflow
    }

    document.body.style.overflow = 'hidden'

    if (appRoot) {
      hadAppAriaHidden.value = appRoot.hasAttribute('aria-hidden')
      previousAppAriaHidden.value = appRoot.getAttribute('aria-hidden')
      appRoot.setAttribute('aria-hidden', 'true')
      appRoot.setAttribute('inert', '')
    }

    return
  }

  document.body.style.overflow = previousBodyOverflow.value ?? ''
  previousBodyOverflow.value = null

  if (appRoot) {
    if (hadAppAriaHidden.value) {
      appRoot.setAttribute('aria-hidden', previousAppAriaHidden.value ?? 'true')
    } else {
      appRoot.removeAttribute('aria-hidden')
    }

    appRoot.removeAttribute('inert')
  }

  hadAppAriaHidden.value = false
  previousAppAriaHidden.value = null
}

const { activate, deactivate } = useFocusTrap(dialogRef, {
  initialFocus: ':self',
  onEscape: () => modal.close(),
})

watch(isOpen, async (open) => {
  if (open) {
    await nextTick()
    activate()
    applyModalSideEffects(true)
    return
  }

  applyModalSideEffects(false)
  const focusAfterClose = modal.consumeFocusAfterClose()
  deactivate()

  if (!focusAfterClose) return

  await nextTick()
  focusAfterClose()?.focus({ preventScroll: true })
})

onBeforeUnmount(() => {
  deactivate()
  applyModalSideEffects(false)
})

function updatePayload(value: unknown) {
  currentPayload.value = value
}

async function runAction(action: ModalStateAction) {
  if (!action.callback) {
    modal.close()
    return
  }

  await action.callback(currentPayload.value)
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 p-4 sm:p-6"
    >
      <button
        type="button"
        class="modal-backdrop"
        aria-label="Close modal"
        @click="modal.close()"
      />

      <div class="pointer-events-none relative z-10 flex min-h-full items-center justify-center">
        <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
          tabindex="-1"
          class="modal-panel"
        >
          <header class="modal-header">
            <h2
              :id="titleId"
              class="min-w-0 flex-1 pr-4 text-2xl font-semibold tracking-tight"
            >
              {{ accessibleTitle }}
            </h2>

            <button
              type="button"
              data-modal-close
              :aria-label="t('common.closeDialog')"
              class="btn-ui btn-ui--ghost btn-ui--icon rounded-full text-muted-foreground"
              @click="modal.close()"
            >
              <UiIcon name="X" class="size-5" />
            </button>
          </header>

          <div class="modal-content">
            <component
              :is="view"
              :data="data"
              @update:modelValue="updatePayload"
            />
          </div>

          <footer class="modal-footer">
            <button
              v-for="action in modalActions"
              :key="action.label"
              type="button"
              data-modal-close
              :aria-label="action.label"
              class="btn-ui"
              :class="action.color ?? 'btn-ui--primary'"
              @click="runAction(action)"
            >
              {{ action.label }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>
